import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '../../mui-styles-bridge.js';
import { Button } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';

const styles = (theme) => ({
  root: {
    width: '100%',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
  },
  canvasContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '600px',
    border: '2px solid #1976d2',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#f9f9f9',
  },
  canvasContainerInactive: {
    position: 'relative',
    width: '100%',
    maxWidth: '600px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#f9f9f9',
  },
  baseImage: {
    display: 'block',
    width: '100%',
    height: 'auto',
  },
  drawingCanvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    cursor: 'crosshair',
  },
  uploadArea: {
    width: '100%',
    maxWidth: '600px',
    minHeight: '200px',
    border: '2px dashed #ccc',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: '#fafafa',
    '&:hover': {
      borderColor: '#999',
      backgroundColor: '#f0f0f0',
    },
  },
  uploadText: {
    color: '#868686',
    fontSize: '14px',
  },
  controls: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  previewContainer: {
    width: '100%',
    maxWidth: '600px',
  },
  previewImage: {
    width: '100%',
    height: 'auto',
    borderRadius: '8px',
    border: '1px solid #ccc',
  },
  hiddenInput: {
    display: 'none',
  },
  annotationBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    border: '1px solid #90caf9',
  },
  annotationHint: {
    fontSize: '12px',
    color: '#1976d2',
    marginTop: '4px',
    fontStyle: 'italic',
  },
});

class ImageAnnotation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      baseImageSrc: null,
      canvasReady: false,
      imageWidth: 0,
      imageHeight: 0,
      isAnnotating: false,
      hasDrawn: false,
    };
    this.signaturePad = null;
    this.baseImageRef = React.createRef();
    this.canvasContainerRef = React.createRef();
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    const { form, data, getDataFromComponent } = this.props;
    const { key, question_text } = data;

    // Restore from saved answer (e.g. returning to a partially filled form)
    const answer_data = form?.answer_data?.[key];
    if (answer_data && answer_data.answer && answer_data.answer[0]) {
      this.setState({ baseImageSrc: answer_data.answer[0], hasDrawn: true });
      return;
    }

    // Load preset image if configured
    const sourceMode = data.sourceMode ?? data.annotationSourceMode ?? data.data?.sourceMode ?? data.data?.annotationSourceMode ?? 'user_upload';
    const presetUrl = data.presetImageUrl || data.data?.presetImageUrl || '';

    if (sourceMode !== 'user_upload' && presetUrl) {
      this.setState({ baseImageSrc: presetUrl });
    }

    // Handle auto/default value
    if (data.data && data.data.toString()) {
      const defaultValue = data.data;
      if (typeof defaultValue === 'string' && defaultValue.startsWith('data:')) {
        this.setState({ baseImageSrc: defaultValue, hasDrawn: true });
        getDataFromComponent(
          { image_annotation_answer: defaultValue },
          key,
          question_text,
          defaultValue
        );
      }
    }

    // Handle auto prefill value
    if (data.auto && typeof data.auto === 'string' && data.auto.startsWith('data:')) {
      this.setState({ baseImageSrc: data.auto, hasDrawn: true });
      getDataFromComponent(
        { image_annotation_answer: data.auto },
        key,
        question_text,
        data.data
      );
    }
  }

  handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.setState({
        baseImageSrc: e.target.result,
        canvasReady: false,
        isAnnotating: false,
        hasDrawn: false,
      });
    };
    reader.readAsDataURL(file);
  };

  handleUploadClick = () => {
    if (this.props.data.read_only) return;
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  handleImageLoad = () => {
    const img = this.baseImageRef.current;
    if (img) {
      this.setState({
        canvasReady: true,
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight,
      });
    }
  };

  initRef = (ref) => {
    this.signaturePad = ref;
    if (this.signaturePad) {
      // Only enable drawing when in annotation mode
      if (!this.state.isAnnotating || this.props.data.read_only) {
        this.signaturePad.off();
      }
    }
  };

  toggleAnnotationMode = () => {
    const wasAnnotating = this.state.isAnnotating;

    if (wasAnnotating) {
      // Exiting annotation mode — composite and emit
      this.compositeAndEmit();
      if (this.signaturePad) {
        this.signaturePad.off();
      }
      this.setState({ isAnnotating: false });
    } else {
      // Entering annotation mode — enable drawing
      this.setState({ isAnnotating: true }, () => {
        if (this.signaturePad) {
          this.signaturePad.on();
        }
      });
    }
  };

  compositeAndEmit = () => {
    const img = this.baseImageRef.current;
    if (!img || !this.signaturePad) return;

    // Check if user has actually drawn anything
    if (this.signaturePad.isEmpty() && !this.state.hasDrawn) {
      return;
    }

    // Create a hidden canvas at the natural image resolution
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');

    // Draw the base image
    ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

    // Draw the annotation layer on top (only if there are strokes)
    if (!this.signaturePad.isEmpty()) {
      const drawingCanvas = this.signaturePad.getCanvas();
      ctx.drawImage(drawingCanvas, 0, 0, img.naturalWidth, img.naturalHeight);
    }

    // Export composited image
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

    this.setState({ hasDrawn: true });

    const { data } = this.props;
    this.props.getDataFromComponent(
      { image_annotation_answer: dataUrl },
      data.key,
      data.question_text,
      data.data
    );
  };

  handleClear = () => {
    if (this.signaturePad) {
      this.signaturePad.clear();
    }
    this.setState({ hasDrawn: false, isAnnotating: false });

    // Reload the original base image for preset mode
    const sourceMode = this.props.data.sourceMode ?? this.props.data.annotationSourceMode ?? this.props.data.data?.sourceMode ?? this.props.data.data?.annotationSourceMode ?? 'user_upload';
    const presetUrl = this.props.data.presetImageUrl || this.props.data.data?.presetImageUrl || '';

    if (sourceMode !== 'user_upload' && presetUrl) {
      this.setState({ baseImageSrc: presetUrl });
    }

    const { data } = this.props;
    this.props.getDataFromComponent(
      { image_annotation_answer: '' },
      data.key,
      data.question_text,
      data.data
    );
  };

  handleReset = () => {
    if (this.signaturePad) {
      this.signaturePad.clear();
    }
    this.setState({
      baseImageSrc: null,
      canvasReady: false,
      isAnnotating: false,
      hasDrawn: false,
    });

    const { data } = this.props;
    this.props.getDataFromComponent(
      { image_annotation_answer: '' },
      data.key,
      data.question_text,
      data.data
    );
  };

  render() {
    const { classes, data } = this.props;
    const { baseImageSrc, canvasReady, isAnnotating, hasDrawn } = this.state;
    const sourceMode = data.sourceMode ?? data.annotationSourceMode ?? data.data?.sourceMode ?? data.data?.annotationSourceMode ?? 'user_upload';
    const isReadOnly = data.read_only;

    try {
      return (
        <div className={classes.root}>
          {baseImageSrc ? (
            /* Show base image — with optional drawing canvas overlay when annotating */
            <div>
              <div
                className={isAnnotating ? classes.canvasContainer : classes.canvasContainerInactive}
                ref={this.canvasContainerRef}
              >
                <img
                  ref={this.baseImageRef}
                  src={baseImageSrc}
                  alt="Image for annotation"
                  className={classes.baseImage}
                  onLoad={this.handleImageLoad}
                  crossOrigin="anonymous"
                />
                {canvasReady && (
                  <SignatureCanvas
                    penColor="red"
                    minWidth={2}
                    maxWidth={3}
                    canvasProps={{
                      className: classes.drawingCanvas,
                      style: {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: isAnnotating ? 'auto' : 'none',
                        cursor: isAnnotating ? 'crosshair' : 'default',
                      },
                    }}
                    ref={this.initRef}
                    backgroundColor="rgba(0,0,0,0)"
                  />
                )}
              </div>
              {isAnnotating && (
                <p className={classes.annotationHint}>
                  Draw on the image to annotate. Click "Done" when finished.
                </p>
              )}
              {!isReadOnly && (
                <div className={classes.controls}>
                  {canvasReady && (
                    <Button
                      size="small"
                      variant={isAnnotating ? 'contained' : 'outlined'}
                      color={isAnnotating ? 'primary' : 'inherit'}
                      onClick={this.toggleAnnotationMode}
                      style={{ textTransform: 'none' }}
                      startIcon={
                        <i className="material-icons" style={{ fontSize: 18 }}>
                          {isAnnotating ? 'check' : 'gesture'}
                        </i>
                      }
                    >
                      {isAnnotating ? 'Done' : 'Annotate'}
                    </Button>
                  )}
                  {hasDrawn && !isAnnotating && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={this.handleClear}
                      style={{ textTransform: 'none' }}
                    >
                      Clear annotations
                    </Button>
                  )}
                  {sourceMode === 'user_upload' && !isAnnotating && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={this.handleReset}
                      style={{ textTransform: 'none' }}
                    >
                      Upload different image
                    </Button>
                  )}
                  {isAnnotating && (
                    <span className={classes.annotationBadge}>
                      <i className="material-icons" style={{ fontSize: 14 }}>gesture</i>
                      Annotation mode
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Show upload area (user_upload mode with no image yet) */
            <div>
              <div
                className={classes.uploadArea}
                onClick={this.handleUploadClick}
              >
                <i className="material-icons" style={{ fontSize: 48, color: '#868686', marginBottom: 8 }}>
                  cloud_upload
                </i>
                <span className={classes.uploadText}>
                  Click to upload an image for annotation
                </span>
                <span style={{ color: '#aaa', fontSize: 12, marginTop: 4 }}>
                  Supports JPG, PNG, GIF, WebP
                </span>
              </div>
              <input
                ref={this.fileInputRef}
                type="file"
                accept="image/*"
                className={classes.hiddenInput}
                onChange={this.handleFileSelect}
              />
            </div>
          )}
        </div>
      );
    } catch (e) {
      return <h5>Error encountered</h5>;
    }
  }
}

const mapStateToProps = (state, props) => {
  return {
    form: state.form,
  };
};

const mapDispatchToProps = {};

ImageAnnotation.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(ImageAnnotation));
